import { Card } from '@braze/web-sdk';
import { track } from '@eventbrite/datalayer-library';
import { getBrazeInstance } from '../client';
import { applyCardTemplateStyles } from './applyCardTemplateStyles';
import { renderCustomNoCardsMessage } from './CustomBrazeNoCardsRenderer';
import { customizeDrawerHeader } from './DrawerHeader';
import './NotificationUIManager.css';
import {
    getSectionIdByTemplate,
    groupCardsAndInsertHeaders,
    removeSectionIfEmpty,
} from './sections';

export function mountBrazeContentCardsUI(
    cards: Card[],
    setIsDrawerOpen: (open: boolean) => void,
): void {
    setTimeout(() => {
        customizeDrawerHeader(setIsDrawerOpen);
        attachDrawerScrollListener();
        attachDrawerDismissHandler(setIsDrawerOpen);

        if (!cards || cards.length === 0) {
            renderCustomNoCardsMessage();
            return;
        }

        // Build a Map of card IDs to card objects
        const cardById = new Map<string, Card>();
        cards.forEach((card) => {
            if (card.id) {
                cardById.set(card.id, card);
            }
        });

        // Group and insert section headers before styling/handlers
        groupCardsAndInsertHeaders(cards);

        const cardElements = document.querySelectorAll('.ab-card');
        cardElements.forEach((cardEl) => {
            // Check for data-eb-card-id (set by grouping) or data-card-id (set by Braze)
            const cardId =
                cardEl.getAttribute('data-eb-card-id') ||
                cardEl.getAttribute('data-card-id');
            if (!cardId) return;
            const card = cardById.get(cardId);
            if (!card) return;
            applyCardTemplateStyles(card, cardEl);
            attachCardEventHandlers(cardEl, card);
        });
    }, 50);
}

function attachCardEventHandlers(cardEl: Element, card: Card): void {
    if ((cardEl as any).__eventHandlersAttached) {
        return;
    }

    const campaignAttributes = getCampaignAttributes(card);
    const cardClickHandler = (event: Event) => {
        const target = event.target as Element;
        if (!target.closest('.ab-close-button')) {
            track({
                eventName: 'NotificationCardClicked',
                eventData: campaignAttributes,
            });
        }
    };

    cardEl.addEventListener('click', cardClickHandler);

    const closeButton = cardEl.querySelector('.ab-close-button');
    if (closeButton) {
        const closeClickHandler = (event: Event) => {
            event.stopPropagation();
            const campaignAttributes = getCampaignAttributes(card);
            track({
                eventName: 'NotificationCloseButtonClicked',
                eventData: campaignAttributes,
            });

            const cardElement = cardEl as HTMLElement;
            const templateNumber = cardElement.dataset.ebTemplate;
            const isPinned = cardElement.dataset.ebPinned === 'true';

            cardElement.remove();

            if (!isPinned && templateNumber) {
                const sectionId = getSectionIdByTemplate(templateNumber);
                if (sectionId) {
                    removeSectionIfEmpty(sectionId);
                }
            }
        };

        closeButton.addEventListener('click', closeClickHandler);
    }

    (cardEl as any).__eventHandlersAttached = true;
}

function attachDrawerScrollListener(): void {
    const feed = document.querySelector('.ab-feed') as HTMLElement;
    if (!feed || feed.classList.contains('listener-attached')) return;

    const handleScroll = () => {
        const isScrolled =
            (document.body.scrollTop || document.documentElement.scrollTop) >
            10;
        feed.classList.toggle('scrolled', isScrolled);
    };

    const scrollContainer = document.querySelector('body');
    scrollContainer?.addEventListener('scroll', handleScroll);
    window.addEventListener('scroll', handleScroll); //for events option from side panel
    feed.classList.add('listener-attached');
    handleScroll();
}

function attachDrawerDismissHandler(
    setIsDrawerOpen: (open: boolean) => void,
): void {
    const drawer = document.querySelector('.ab-feed');
    const notificationIcon = document.querySelector(
        '[data-testid="notification-icon"]',
    );
    if (!drawer) return;

    if ((drawer as any).__dismissHandlerAttached) {
        return;
    }

    const handleClickOutside = async (event: MouseEvent) => {
        const target = event.target as Node | null;
        if (
            target &&
            !drawer.contains(target) &&
            !notificationIcon?.contains(target)
        ) {
            track({
                eventName: 'NotificationDrawerCloseClicked',
                eventData: {},
            });
            const braze = await getBrazeInstance();
            braze.hideContentCards();
            document.removeEventListener('mousedown', handleClickOutside);
            setIsDrawerOpen(false);
            (drawer as any).__dismissHandlerAttached = false;
        }
    };

    document.addEventListener('mousedown', handleClickOutside);
    (drawer as any).__dismissHandlerAttached = true;
}

function getCampaignAttributes(card: Card): Record<string, string> {
    const { campaign_name, campaign_id, variant_name, step_name } =
        card?.extras || {};

    return {
        campaign_name: campaign_name || 'NA',
        campaign_id: campaign_id || 'NA',
        variant_name: variant_name || 'NA',
        step_name: step_name || 'NA',
    };
}
